// src/utils/threeUtils.ts
import * as THREE from 'three';
// import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js'; // Not used here
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// WGS84 ellipsoid parameters
const WGS84_A = 6378137.0; // Semi-major axis (equatorial radius) in meters
const WGS84_F = 1 / 298.257223563; // Flattening
const WGS84_B = WGS84_A * (1 - WGS84_F); // Semi-minor axis (polar radius)
const WGS84_E2 = 2 * WGS84_F - WGS84_F * WGS84_F; // Square of first eccentricity

/**
 * Converts Geodetic coordinates (latitude, longitude, altitude) to ECEF (Earth-Centered, Earth-Fixed) coordinates.
 * Input: [latitude (degrees), longitude (degrees), altitude (meters)]
 * Output: THREE.Vector3 { x, y, z } in ECEF meters
 */
export function convertFromExternalCoords(coords: [number, number] | [number, number, number]): THREE.Vector3 {
    const lat = coords[0];
    const lon = coords[1];
    const alt = coords.length > 2 ? coords[2]! : 0; // Altitude above the WGS84 ellipsoid

    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinLon = Math.sin(lonRad);
    const cosLon = Math.cos(lonRad);

    // Radius of curvature in the prime vertical
    const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);

    // Calculate ECEF coordinates
    const x = (N + alt) * cosLat * cosLon;
    const y = (N + alt) * cosLat * sinLon;
    const z = ((WGS84_B * WGS84_B) / (WGS84_A * WGS84_A) * N + alt) * sinLat;
    // const z = ((1 - WGS84_E2) * N + alt) * sinLat; // Equivalent alternative calculation for z

    return new THREE.Vector3(x, y, z);
}


/**
 * Converts ECEF coordinates [x, y, z] used by the API/backend
 * to the internal Three.js world coordinate system.
 * This mapping depends on how the 3D scene is oriented relative to ECEF.
 * Common convention: ECEF X -> Three +X, ECEF Y -> Three -Z, ECEF Z -> Three +Y (Y-up)
 * Input: [x_ecef, y_ecef, z_ecef]
 * Output: Three.js Vector3(x_three, y_three, z_three)
 */
export const convertToInternalCoords = (ecefCoords: [number, number, number]): THREE.Vector3 => {
  // Assuming Y-up convention for Three.js scene relative to ECEF:
  return new THREE.Vector3(
    ecefCoords[0], // ECEF X maps to Three X
    ecefCoords[2], // ECEF Z maps to Three Y (Up)
   -ecefCoords[1]  // ECEF Y maps to Three -Z (Depth/Forward)
  );
};

/**
 * Converts internal Three.js world coordinates Vector3(x, y, z)
 * back to ECEF coordinates [x, y, z] for the API/backend.
 * This is the inverse of convertToInternalCoords.
 * Input: Three.js Vector3(x_three, y_three, z_three)
 * Output: ECEF [x_ecef, y_ecef, z_ecef]
 */
export const convertToExternalCoords = (worldVector: THREE.Vector3): [number, number, number] => {
  // Assuming Y-up convention:
  return [
    worldVector.x,    // Three X maps to ECEF X
   -worldVector.z,   // Three -Z maps to ECEF Y
    worldVector.y     // Three Y maps to ECEF Z
  ];
};

/**
 * Converts world coordinates to local coordinates relative to a specified world origin.
 * Helps avoid floating point precision issues when dealing with large world coordinates (like ECEF).
 * The local origin MUST be in world coordinates.
 */
export const worldToLocal = (worldPoint: THREE.Vector3, localOriginWorld: THREE.Vector3 | null): THREE.Vector3 => {
  if (!localOriginWorld) {
      console.warn("worldToLocal called without localOriginWorld, returning original worldPoint.");
      return worldPoint.clone();
  }
  return new THREE.Vector3().subVectors(worldPoint, localOriginWorld);
};

/**
 * Converts local coordinates (relative to a world origin) back to world coordinates.
 * Inverse of worldToLocal function.
 * The local origin MUST be in world coordinates.
 */
export const localToWorld = (localPoint: THREE.Vector3, localOriginWorld: THREE.Vector3 | null): THREE.Vector3 => {
  if (!localOriginWorld) {
       console.warn("localToWorld called without localOriginWorld, returning original localPoint.");
      return localPoint.clone();
    }
  return new THREE.Vector3().addVectors(localPoint, localOriginWorld);
};

/**
 * Creates a Material UI-style text sprite label.
 */
export const createTextSprite = (text: string): THREE.Sprite => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get canvas context');
    
    // Styling constants
    const backgroundColor = '#F6B12D';
    const textColor = '#000000';
    const outlineColor = '#000000';
    const outlineWidth = 2;
    const fontSize = 36;
    const fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const paddingX = 40;
    const paddingY = 20;
    
    // Set up font first to measure text
    context.font = `bold ${fontSize}px ${fontFamily}`;
    
    // Measure text to determine canvas size
    const textMetrics = context.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = fontSize;
    
    // Calculate canvas dimensions based on text size
    canvas.width = textWidth + paddingX * 2;
    canvas.height = textHeight + paddingY * 2;
    
    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Re-set font after canvas resize
    context.font = `bold ${fontSize}px ${fontFamily}`;
    
    // Calculate pill radius (half the height)
    const pillRadius = canvas.height / 2;
    
    // Draw pill shape with outline
    context.fillStyle = backgroundColor;
    context.strokeStyle = outlineColor;
    context.lineWidth = outlineWidth;
    
    // Create pill shape path
    context.beginPath();
    context.arc(pillRadius, pillRadius, pillRadius - outlineWidth/2, Math.PI/2, 3*Math.PI/2);
    context.lineTo(canvas.width - pillRadius, outlineWidth/2);
    context.arc(canvas.width - pillRadius, pillRadius, pillRadius - outlineWidth/2, 3*Math.PI/2, Math.PI/2);
    context.lineTo(pillRadius, canvas.height - outlineWidth/2);
    context.closePath();
    
    // Fill and stroke the pill
    context.fill();
    context.stroke();
    
    // Draw text (centered)
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = textColor;
    context.fillText(text, canvas.width / 2, canvas.height / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(spriteMaterial);
    
    // Scale sprite proportionally to canvas size
    const scale = 0.02; // Adjust this to control overall size
    sprite.scale.set(canvas.width * scale, canvas.height * scale, 1);

    // *** FIX: Increased render order to ensure it's drawn on top of markers ***
    sprite.renderOrder = 3000; 

    return sprite;
};

/**
 * Cleans up Three.js resources associated with an object and removes it from parent.
 * Safe disposal of geometries, materials, and textures.
 */
export const disposeObject3D = (object: THREE.Object3D) => {
  if (!object) return;

  // Ensure it's removed from its parent first
  if (object.parent) {
    object.parent.remove(object);
  }

  // Dispose children first recursively
  if ((object as any).isGroup || object instanceof THREE.Scene) {
      // Make a copy of the children array before iterating because children might be removed during disposal
      const children = [...object.children];
      children.forEach(disposeObject3D);
  }

  // Dispose geometry
  if ((object as any).geometry && typeof (object as any).geometry.dispose === 'function') {
      try {
        (object as any).geometry.dispose();
      } catch (err) {
           console.warn("Error disposing geometry:", err, object);
      }
  }

  // Dispose material(s)
  if ((object as any).material) {
    const materials = Array.isArray((object as any).material) ? (object as any).material : [(object as any).material];
    materials.forEach((material: any) => {
      if (!material) return;
      try {
          // Dispose textures used by the material
          Object.values(material).forEach((value: any) => {
            if (value instanceof THREE.Texture && typeof value.dispose === 'function') {
              value.dispose();
            }
          });
          // Dispose the material itself
          if (typeof material.dispose === 'function') {
             material.dispose();
          }
      } catch (err) {
           console.warn("Error disposing material or its textures:", err, material);
      }
    });
  }

  // Remove any event listeners if applicable (less common for basic objects)
  if (typeof (object as any).clear === 'function') { // Some objects might have a clear method
     (object as any).clear();
  }
};

/**
 * Updates the resolution of LineMaterial instances in a scene.
 * Necessary when the canvas resizes.
 */
export const updateLineMaterialsResolution = (scene: THREE.Scene, width: number, height: number) => {
    scene.traverse((object) => {
        if (object instanceof Line2 && object.material instanceof LineMaterial) {
             object.material.resolution.set(width, height);
        }
    });
};